import React, { useMemo } from "react";
import styled from "styled-components";
import moment from "moment";
import { UI_CAPTION, colors2 } from "oolib";
import { TrendLineChart } from "@/_self_managed/SM_Dashboard/LensesV2/comps/TrendLineChart";
import { binsForPeriod } from "./CategorySparklines";

/**
 * Right-sidebar companion to the drill-down donut: ONE trend chart with a
 * line per segment of the donut's CURRENT level, so the segments can be read
 * against each other, then a legend (dot + name + count) below it, biggest
 * first. The biggest segment's line draws at full colour and the rest sit
 * dimmed until hovered — the same emphasis the cards' category sparklines
 * use. Drilling the donut swaps both to the new level (the chart reports its
 * path via onActivePathChange; DonutLayout resolves it to this level's nodes).
 *
 * The drill trail itself renders as the page-level breadcrumb above the
 * widgets (see TopicClusteringTreeView), not in here.
 */

const Panel = styled.div`
  padding: 1.2rem;
  height: 100%;
  overflow-y: auto;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 0.8rem;
`;

const ItemTitle = styled(UI_CAPTION)`
  color: ${colors2.grey60};
`;

const ItemLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  &:hover ${ItemTitle} {
    color: ${colors2.grey80};
    text-decoration: underline;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const DonutLevelPanel = ({ nodes = [], period, onDrill, onBinClick }) => {
  const sorted = useMemo(
    () => [...nodes].sort((a, b) => (b.observations?.length || 0) - (a.observations?.length || 0)),
    [nodes]
  );

  // Every node rides in the SAME bucket so TrendLineChart lands them on one
  // chart. Series are keyed by node id (the pivoted dataKey), while `display`
  // is the label — it names the line in the tooltip and keys `colors`.
  const series = useMemo(() => {
    const binned = binsForPeriod(period);
    if (!binned || !sorted.length) return null;
    const countsByNode = {};
    let any = false;
    for (const n of sorted) {
      const counts = {};
      for (const o of n.observations || []) {
        if (!o.dateThatMatters) continue;
        const key = moment(o.dateThatMatters).format(binned.fmt);
        counts[key] = (counts[key] || 0) + 1;
        any = true;
      }
      countsByNode[n.id] = counts;
    }
    if (!any) return null;
    return {
      data: binned.bins.map((key) => ({
        timeInterval: key,
        dimensions: sorted.map((n) => ({
          value: n.id,
          display: n.label,
          count: countsByNode[n.id][key] || 0,
        })),
      })),
      colors: Object.fromEntries(sorted.map((n) => [n.label, n.color])),
      // LineChart matches highlightLine against the pivoted dataKey `${value}_count`.
      highlight: `${sorted[0].id}_count`,
    };
  }, [sorted, period]);

  if (!sorted.length) return null;

  return (
    <Panel>
      {series && (
        <TrendLineChart
          data={series.data}
          interval={period.interval}
          height={160}
          colors={series.colors}
          highlight={series.highlight}
          highlightStrokeBoost={0}
          defaultOpacity={1}
          hideXAxisTicks
          hideGrid
          margin={{ top: 6, right: 4, bottom: 6, left: 4 }}
          {...(onBinClick ? { onXClick: (x) => onBinClick(String(x)) } : {})}
        />
      )}
      <Legend>
        {sorted.map((n) => (
          // Same action as clicking the node's segment on the donut.
          <ItemLabel key={n.id} onClick={() => onDrill?.(n)}>
            <Dot $color={n.color} />
            <ItemTitle>
              {n.label} · {n.observations?.length || 0}
            </ItemTitle>
          </ItemLabel>
        ))}
      </Legend>
    </Panel>
  );
};
